import {
  ConfigProvider,
  Form,
  Input,
  Modal,
  Select,
  Upload,
  UploadFile,
  UploadProps,
} from "antd";
import { CreateTicketProps } from "./create-ticket.props";
import { useCallback, useRef, useState } from "react";
import { useForm } from "antd/es/form/Form";
import { axios } from "../../../api/axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import { AxiosError, AxiosResponse } from "axios";
import toast from "react-hot-toast";
import { styled } from "styled-components";
import FormItem from "antd/es/form/FormItem";
import { FilmProfile } from "../../../models/film-profile.model";
import { BusinessProfile } from "../../../models/business-profile.model";
import { useNavigate } from "react-router-dom";
import html2canvas from "@cantoo/html2canvas";
import { v4 } from "uuid";
import {
  MdHelpOutline,
  MdOutlineEmail,
  MdCloudUpload,
  MdArrowForward,
  MdScreenshotMonitor,
} from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import { colors } from "../../../theme/colors";
import { wait } from "../../../utils/functions/misc/wait";

export const CreateTicket = ({
  open,
  setOpen,
  onCreated,
}: CreateTicketProps) => {
  const navigate = useNavigate();

  const [isEntitiesVisible, setIsEntitiesVisible] = useState(false);
  const [entities, setEntities] = useState<{ label: string; value: string }[]>(
    []
  );
  const [screenshots, setScreenshots] = useState<{ url: string; file: File }[]>(
    []
  );
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [description, setDescription] = useState("");
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const screenshotBusyRef = useRef(false);

  const beforeUpload = (file: File) => {
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("You can only upload JPG/PNG files!");
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > 10) {
      toast.error("Image must be smaller than 10MB!");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customRequest = (options: any) => {
    const { onSuccess, file } = options;
    const url = URL.createObjectURL(file);
    onSuccess([url, file]);
  };

  const handleUploadChange: UploadProps["onChange"] = ({
    file,
    fileList: newList,
  }) => {
    setFileList(newList);
    if (file.status === "done" && Array.isArray(file.response)) {
      setScreenshots((prev) => [
        ...prev,
        { url: file.response[0], file: file.response[1] },
      ]);
    }
  };

  const handleUploadRemove = (file: UploadFile) => {
    const url = (file.response as [string, File] | undefined)?.[0] ?? file.url;
    setScreenshots((prev) => prev.filter((s) => s.url !== url));
  };

  const takePageScreenshot = useCallback(async () => {
    if (screenshotBusyRef.current || screenshots.length >= 3) {
      if (screenshots.length >= 3) {
        toast.error("You can attach up to 3 images.");
      }
      return;
    }
    screenshotBusyRef.current = true;
    setIsCapturingScreenshot(true);
    try {
      setOpen(false);
      await wait(500);

      const canvas = await html2canvas(document.body, {
        allowTaint: true,
        useCORS: true,
      });

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
      });

      if (!blob) {
        toast.error("Could not create screenshot.");
        return;
      }

      const id = v4();
      const file = new File([blob], `screenshot-${id.slice(0, 8)}.jpeg`, {
        type: "image/jpeg",
      });
      const url = URL.createObjectURL(file);

      setScreenshots((prev) => [...prev, { url, file }]);
      setFileList((prev) => [
        ...prev,
        {
          uid: id,
          name: file.name,
          status: "done",
          url,
          thumbUrl: url,
          response: [url, file] as [string, File],
        },
      ]);
      toast.success("Screenshot added");
    } catch (e) {
      console.error(e);
      toast.error("Could not capture screenshot.");
    } finally {
      screenshotBusyRef.current = false;
      setIsCapturingScreenshot(false);
      setOpen(true);
    }
  }, [screenshots.length, setOpen]);

  const [form] = useForm<{
    title: string;
    description: string;
    feature: string;
    action: string;
    other?: string;
    object_id?: string;
  }>();

  const onSubmit = useCallback(async () => {
    try {
      await form.validateFields();
      if (!description) {
        return form.setFields([
          { name: "description", errors: ["Please enter a description!"] },
        ]);
      }

      const ticket = form.getFieldsValue();
      ticket.description = description;

      const formData = new FormData();
      formData.append("title", ticket.title);
      formData.append("description", ticket.description);
      formData.append("content_type", ticket.feature);
      if (ticket.object_id) formData.append("object_id", ticket.object_id);

      screenshots.forEach(({ file }) => formData.append("files", file));

      const resp = await axios.post("/client/tickets", formData);
      handleResponse(resp, () => {
        navigate(`/t/${resp.data.id}`);
      });

      onCreated();
    } catch (e) {
      if (e instanceof AxiosError) {
        handleResponse(e);
      } else {
        console.log(e);
        toast.error("Please fix the errors in the form!");
      }
    }
  }, [screenshots, description]);

  return (
    <StyledModal
      open={open}
      okText="Submit Ticket"
      cancelText="Cancel"
      onOk={onSubmit}
      onCancel={() => setOpen(false)}
      width={600}
      centered
      destroyOnClose
      title={null}
      styles={{
        mask: {
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          backgroundColor: "rgba(7, 18, 29, 0.45)",
        },
      }}
      okButtonProps={{
        style: {
          height: "3.6rem",
          paddingInline: "2rem",
          fontWeight: 600,
          fontSize: "1.3rem",
          borderRadius: "0.8rem",
        },
      }}
      cancelButtonProps={{
        style: {
          height: "3.6rem",
          paddingInline: "2rem",
          fontSize: "1.3rem",
          borderRadius: "0.8rem",
        },
      }}
    >
      <Header>
        <IconBadge>
          <MdHelpOutline size={24} />
        </IconBadge>
        <HeaderText>
          <HeaderTitle>How can we help?</HeaderTitle>
          <HeaderSubtitle>
            Tell us what's going on and we'll get back to you shortly.
          </HeaderSubtitle>
        </HeaderText>
      </Header>

      <Body>
        <StyledForm form={form} layout="vertical" requiredMark={false}>
          <FieldRow>
            <StyledFormItem
              label="Category"
              name="feature"
              required
              rules={[{ required: true, message: "Please select a category" }]}
            >
              <Select
                placeholder="Where is the issue?"
                showSearch
                options={[
                  { label: "Films", value: "film" },
                  { label: "Series", value: "series" },
                  {
                    label: "Professional Profile",
                    value: "professionalprofile",
                  },
                  { label: "Business Profile", value: "businessprofile" },
                  { label: "Account and Settings", value: "pocketuser" },
                ]}
                onSelect={async (value) => {
                  let resp:
                    | AxiosResponse<FilmProfile[]>
                    | AxiosResponse<{ profile: BusinessProfile }>;
                  setIsEntitiesVisible(true);
                  switch (value) {
                    case "film":
                      resp = await axios.get<FilmProfile[]>(
                        "/client/get/films/me"
                      );
                      setEntities(
                        resp.data.map((film) => ({
                          label: film.title,
                          value: film.id,
                        }))
                      );
                      break;
                    case "series":
                      break;
                    default:
                      setEntities([]);
                      setIsEntitiesVisible(false);
                      break;
                  }
                }}
              />
            </StyledFormItem>

            <StyledFormItem
              label="Issue Title"
              name="title"
              required
              rules={[
                { required: true, message: "Please enter an issue title" },
              ]}
            >
              <Input placeholder="A short summary of the problem" />
            </StyledFormItem>
          </FieldRow>

          {isEntitiesVisible ? (
            <ConfigProvider renderEmpty={() => "No options available"}>
              <StyledFormItem label="Project" name="object_id">
                <Select
                  placeholder="Select the affected project"
                  showSearch
                  options={entities}
                />
              </StyledFormItem>
            </ConfigProvider>
          ) : null}

          <StyledFormItem
            label="Description"
            name="description"
            required
            rules={[{ required: true, message: "Please enter a description" }]}
          >
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what happened, what you expected, and any steps to reproduce."
              rows={3}
            />
          </StyledFormItem>

          <StyledFormItem
            label={
              <AttachLabelRow>
                <span>Attachments</span>
                <AttachHint>
                  {screenshots.length}/3 · JPG/PNG · max 10MB
                </AttachHint>
              </AttachLabelRow>
            }
            name="screenshot"
          >
            <StyledDragger
              name="screenshot"
              multiple
              maxCount={3}
              accept="image/jpeg,image/png"
              fileList={fileList}
              beforeUpload={beforeUpload}
              customRequest={customRequest}
              onChange={handleUploadChange}
              onRemove={handleUploadRemove}
              listType="picture"
            >
              <DragInner>
                <MdCloudUpload size={28} />
                <DragText>
                  Click to choose a file or drag it here
                </DragText>
              </DragInner>
            </StyledDragger>
            <ScreenshotCaptureRow>
              <ScreenshotCaptureButton
                type="button"
                disabled={screenshots.length >= 3 || isCapturingScreenshot}
                onClick={takePageScreenshot}
              >
                <MdScreenshotMonitor size={22} />
                {isCapturingScreenshot ? "Capturing…" : "Capture page screenshot"}
              </ScreenshotCaptureButton>
              <ScreenshotCaptureHint>
                Closes this dialog briefly so the capture shows the page behind it.
              </ScreenshotCaptureHint>
            </ScreenshotCaptureRow>
          </StyledFormItem>
        </StyledForm>

        <Divider>
          <DividerLine />
          <DividerText>or reach us directly</DividerText>
          <DividerLine />
        </Divider>

        <ContactGrid>
          <ContactCard
            href="mailto:info@pocketfilms.in"
            target="_blank"
            rel="noreferrer"
            $variant="email"
          >
            <ContactIcon $variant="email">
              <MdOutlineEmail size={22} />
            </ContactIcon>
            <ContactText>
              <ContactLabel>Email us</ContactLabel>
              <ContactValue>info@pocketfilms.in</ContactValue>
            </ContactText>
            <ContactArrow>
              <MdArrowForward size={18} />
            </ContactArrow>
          </ContactCard>

          <ContactCard
            href="https://wa.me/+917700904810"
            target="_blank"
            rel="noreferrer"
            $variant="whatsapp"
          >
            <ContactIcon $variant="whatsapp">
              <FaWhatsapp size={22} />
            </ContactIcon>
            <ContactText>
              <ContactLabel>WhatsApp us</ContactLabel>
              <ContactValue>+91 77009 04810</ContactValue>
            </ContactText>
            <ContactArrow>
              <MdArrowForward size={18} />
            </ContactArrow>
          </ContactCard>
        </ContactGrid>
      </Body>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  max-width: calc(100vw - 2.4rem);

  .ant-modal-content {
    border-radius: 1.4rem;
    padding: 0;
    overflow: hidden;
    background-color: ${colors.background.hex};
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-footer {
    padding: 1.2rem 2.4rem 1.6rem;
    margin-top: 0;
    border-top: 1px solid ${colors.grey.withOpacity(0.4).hex};
  }

  @media only screen and (max-width: 36em) {
    .ant-modal-footer {
      padding: 1rem 1.6rem 1.4rem;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  padding: 1.8rem 2.4rem;
  background-color: ${colors.primary.hex};

  @media only screen and (max-width: 36em) {
    padding: 1.4rem 1.6rem;
    gap: 1rem;
  }
`;

const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  background-color: rgba(255, 255, 255, 0.16);
  color: ${colors.white.hex};
  flex-shrink: 0;

  @media only screen and (max-width: 36em) {
    width: 3.4rem;
    height: 3.4rem;
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

const HeaderTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${colors.white.hex};
  margin: 0;
  line-height: 1.2;

  @media only screen and (max-width: 36em) {
    font-size: 1.6rem;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.82);
  margin: 0;
  line-height: 1.4;

  @media only screen and (max-width: 36em) {
    font-size: 1.2rem;
  }
`;

const Body = styled.div`
  padding: 1.8rem 2.4rem 0.8rem;

  @media only screen and (max-width: 36em) {
    padding: 1.4rem 1.6rem 0.6rem;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 1.2rem;
  }

  .ant-form-item-label {
    padding-bottom: 0.5rem;
    line-height: 1.2;
  }

  .ant-form-item-label > label {
    font-size: 1.2rem;
    font-weight: 600;
    color: ${colors.text.withOpacity(0.85).hex};
    height: 1.6rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector,
  textarea.ant-input {
    border-radius: 1rem !important;
    border: 1.5px solid ${colors.primary.withOpacity(0.35).hex} !important;
    background-color: ${colors.lightBackground.hex} !important;
    font-size: 1.35rem;
    transition: all 0.18s ease;
    box-shadow: none !important;
  }

  .ant-input:hover,
  .ant-input-affix-wrapper:hover,
  .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
  textarea.ant-input:hover {
    border-color: ${colors.primary.withOpacity(0.7).hex} !important;
  }

  .ant-input:focus,
  .ant-input-focused,
  .ant-input-affix-wrapper-focused,
  .ant-select-focused .ant-select-selector,
  textarea.ant-input:focus {
    border-color: ${colors.primary.hex} !important;
    box-shadow: 0 0 0 3px ${colors.primary.withOpacity(0.15).hex} !important;
  }

  .ant-input,
  .ant-select-single .ant-select-selector {
    height: 4.4rem;
  }

  .ant-select-single .ant-select-selector {
    padding: 0 1.4rem;
  }

  .ant-select-single .ant-select-selector .ant-select-selection-item,
  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    line-height: 4.2rem;
  }

  .ant-input {
    padding: 0 1.4rem;
  }

  textarea.ant-input {
    height: auto;
    min-height: 8rem;
    padding: 1rem 1.4rem;
    line-height: 1.5;
    resize: none;
  }

  .ant-form-item-explain-error {
    font-size: 1.15rem;
    margin-top: 0.4rem;
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 1.2rem;
  align-items: start;

  @media only screen and (max-width: 36em) {
    grid-template-columns: 1fr;
  }
`;

const StyledFormItem = styled(FormItem)`
  width: 100%;
`;

const AttachLabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
`;

const AttachHint = styled.span`
  font-size: 1.1rem;
  font-weight: 400;
  color: ${colors.text.withOpacity(0.55).hex};
`;

const StyledDragger = styled(Upload.Dragger)`
  &.ant-upload-wrapper .ant-upload.ant-upload-drag {
    background-color: ${colors.lightBackground.hex};
    border: 1.5px dashed ${colors.primary.withOpacity(0.35).hex};
    border-radius: 0.9rem;
    padding: 1.4rem 1.2rem;
    transition: all 0.15s ease;
  }

  &.ant-upload-wrapper .ant-upload.ant-upload-drag:hover,
  &.ant-upload-wrapper .ant-upload.ant-upload-drag-hover {
    border-color: ${colors.primary.hex};
    background-color: ${colors.primary.withOpacity(0.04).hex};
  }

  &.ant-upload-wrapper .ant-upload-list {
    margin-top: 0.6rem;
  }

  &.ant-upload-wrapper .ant-upload-list-item {
    border-radius: 0.6rem;
    padding: 0.4rem 0.6rem;
  }
`;

const DragInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.text.withOpacity(0.7).hex};
`;

const DragText = styled.span`
  font-size: 1.3rem;
  font-weight: 500;
  color: ${colors.text.hex};
`;

const ScreenshotCaptureRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-top: 0.75rem;
`;

const ScreenshotCaptureButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  width: 100%;
  padding: 0.85rem 1.2rem;
  border-radius: 1rem;
  border: 1.5px dashed ${colors.primary.withOpacity(0.45).hex};
  background-color: ${colors.lightBackground.hex};
  color: ${colors.text.hex};
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    opacity 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${colors.primary.hex};
    background-color: ${colors.primary.withOpacity(0.06).hex};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ScreenshotCaptureHint = styled.span`
  font-size: 1.1rem;
  font-weight: 400;
  color: ${colors.text.withOpacity(0.55).hex};
  text-align: center;
  line-height: 1.35;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 0.4rem 0 1rem;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${colors.grey.withOpacity(0.6).hex};
`;

const DividerText = styled.span`
  font-size: 1.1rem;
  font-weight: 500;
  color: ${colors.text.withOpacity(0.55).hex};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;
  gap: 1rem;
  margin-bottom: 1rem;

  @media only screen and (max-width: 36em) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.a<{ $variant: "email" | "whatsapp" }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.4rem;
  height: 100%;
  min-height: 7rem;
  box-sizing: border-box;
  border-radius: 1.1rem;
  border: 1.5px solid ${colors.primary.withOpacity(0.3).hex};
  background-color: ${colors.lightBackground.hex};
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  min-width: 0;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 0.4rem;
    background-color: ${({ $variant }) =>
      $variant === "whatsapp" ? "#25D366" : colors.primary.hex};
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    border-color: ${({ $variant }) =>
      $variant === "whatsapp" ? "#25D366" : colors.primary.hex};
    transform: translateY(-2px);
    box-shadow: 0 0.6rem 1.6rem
      ${({ $variant }) =>
        $variant === "whatsapp"
          ? "rgba(37, 211, 102, 0.18)"
          : colors.primary.withOpacity(0.18).hex};
  }

  &:hover::before {
    opacity: 1;
  }
`;

const ContactIcon = styled.div<{ $variant: "email" | "whatsapp" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  flex-shrink: 0;
  color: ${colors.white.hex};
  background-color: ${({ $variant }) =>
    $variant === "whatsapp" ? "#25D366" : colors.primary.hex};
  box-shadow: 0 0.3rem 0.8rem
    ${({ $variant }) =>
      $variant === "whatsapp"
        ? "rgba(37, 211, 102, 0.28)"
        : colors.primary.withOpacity(0.28).hex};
`;

const ContactText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.2rem;
  flex: 1;
  min-width: 0;
`;

const ContactLabel = styled.span`
  font-size: 1.05rem;
  font-weight: 600;
  color: ${colors.text.withOpacity(0.55).hex};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1.3;
`;

const ContactValue = styled.span`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${colors.text.hex};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const ContactArrow = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  background-color: ${colors.grey.withOpacity(0.4).hex};
  color: ${colors.text.withOpacity(0.55).hex};
  transition: all 0.2s ease;
  flex-shrink: 0;

  ${ContactCard}:hover & {
    background-color: ${colors.primary.hex};
    color: ${colors.white.hex};
    transform: translateX(2px);
  }
`;
